import React, { useEffect, useRef, useState } from "react";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
// StarterKit v3 bundles link + underline; we rely on those built-ins rather
// than re-importing the standalone extensions (which would duplicate).
import { Box, Text } from "@mantine/core";

export const DESCRIPTION_PLAINTEXT_SOFT_CAP = 1024;
export const DESCRIPTION_HTML_HARD_CAP = 8000;

interface ServiceDescriptionEditorProps {
  value?: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  minHeight?: number;
}

const ServiceDescriptionEditor: React.FC<ServiceDescriptionEditorProps> = ({
  value = "",
  onChange,
  label,
  placeholder = "Describe this service...",
  error,
  minHeight = 140,
}) => {
  // Track the HTML we last wrote into the editor so the value-sync effect
  // doesn't overwrite content (and reset the cursor) when the upstream value
  // only differs from the editor's canonical HTML by whitespace/attribute order.
  const lastSetRef = useRef<string>(value);
  const [plaintextLength, setPlaintextLength] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        link: { openOnClick: false, autolink: true },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastSetRef.current = html;
      setPlaintextLength(editor.getText().length);
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    // Only sync when the upstream value diverges from what we last set. This
    // catches the pre-filled-edit-modal case without fighting the editor
    // during active typing.
    if (value !== lastSetRef.current) {
      lastSetRef.current = value;
      editor.commands.setContent(value || "", { emitUpdate: false });
      setPlaintextLength(editor.getText().length);
    }
  }, [value, editor]);

  const overSoftCap = plaintextLength > DESCRIPTION_PLAINTEXT_SOFT_CAP;

  return (
    <Box>
      {label && (
        <Box
          component="label"
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 500,
            marginBottom: "8px",
            color: "var(--mantine-color-text)",
          }}
        >
          {label}
        </Box>
      )}
      <RichTextEditor editor={editor}>
        <RichTextEditor.Toolbar sticky stickyOffset={60}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.ClearFormatting />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H2 />
            <RichTextEditor.H3 />
            <RichTextEditor.H4 />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>
        <RichTextEditor.Content style={{ minHeight: `${minHeight}px` }} />
      </RichTextEditor>
      <Box
        mt={4}
        style={{ display: "flex", justifyContent: "space-between", gap: 12 }}
      >
        <Text c={error ? "red" : "dimmed"} size="xs">
          {error ?? " "}
        </Text>
        <Text c={overSoftCap ? "red" : "dimmed"} size="xs">
          {plaintextLength} / {DESCRIPTION_PLAINTEXT_SOFT_CAP} characters
        </Text>
      </Box>
    </Box>
  );
};

export default ServiceDescriptionEditor;
